'use client';

import type { AppRouter } from './root';
import type { inferRouterInputs, inferRouterOutputs } from '@trpc/server';

import { env } from '@mcdougald/env';
import { type QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import {
  createTRPCClient,
  httpBatchStreamLink,
  loggerLink,
} from '@trpc/client';
import { createTRPCContext } from '@trpc/tanstack-react-query';
import { type ReactNode, useState } from 'react';
import { SuperJSON } from 'superjson';

import { DOMAIN } from '@/constants/data/urls';

import { makeQueryClient } from './query-client';

export const { TRPCProvider, useTRPC } = createTRPCContext<AppRouter>();

let browserQueryClient: QueryClient | undefined;

const getBaseOldUrl = () => {
  if (typeof globalThis !== 'undefined') return ''; // browser should use relative url
  if (env.VERCEL_URL) return `https://${env.VERCEL_URL}`; // SSR should use vercel url
  return `http://localhost:${process.env.PORT ?? 3000}`; // dev SSR should use localhost
};

const getBaseUrl = () => {
  // if (process.env.VERCEL_TARGET_ENV === "preview") {
  //   return `https://${process.env.VERCEL_URL}`;
  // }
  if (env.NODE_ENV === 'development') {
    return 'https://localhost:3000';
  } else if (env.NODE_ENV === 'production') {
    return (
      env.NEXT_PUBLIC_URL ??
      process.env.VERCEL_PROJECT_PRODUCTION_URL ??
      `https://${DOMAIN}`
    );
  } else {
    console.warn('Unknown baseUrl Auth environment. Defaulting to localhost.');
    return getBaseOldUrl();
  }
};

const getQueryClient = () => {
  if (typeof globalThis === 'undefined') {
    return makeQueryClient();
  }

  browserQueryClient ??= makeQueryClient();

  return browserQueryClient;
};

export type RouterInputs = inferRouterInputs<AppRouter>;
export type RouterOutputs = inferRouterOutputs<AppRouter>;

type TRPCReactProviderProps = {
  children: ReactNode;
};

export const TRPCReactProvider = (props: TRPCReactProviderProps) => {
  const { children } = props;
  const queryClient = getQueryClient();

  // eslint-disable-next-line @eslint-react/naming-convention/use-state -- it's fine
  const [trpcClient] = useState(() =>
    createTRPCClient<AppRouter>({
      links: [
        loggerLink({
          enabled: (op) =>
            process.env.NODE_ENV === 'development' ||
            (op.direction === 'down' && op.result instanceof Error),
        }),
        httpBatchStreamLink({
          transformer: SuperJSON,
          url: `${getBaseOldUrl()}/api/trpc`,
          fetch(input, init) {
            return fetch(input, { ...init, credentials: 'include' })
          },
          headers: () => {
            const headers = new Headers();
            headers.set('x-trpc-source', 'nextjs-react');
            return headers;
          },
        }),
      ],
    }),
  );

  return (
    <QueryClientProvider client={queryClient}>
      <TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
        {children}
        <ReactQueryDevtools />
      </TRPCProvider>
    </QueryClientProvider>
  );
};
